import React, { useEffect } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { notifySuccess } from "../../../../components/AppToast";
import { staffApiError } from "../apiErrors";
import { useDeactivateStaff } from "../hooks";

const HEADING_ID = "deactivate-staff-modal-title";

/**
 * Confirmación de desactivación (PLATFORM_ADMIN).
 *
 * Es un modal y no un `window.confirm` porque los 409 anti-bloqueo del backend
 * (`staff_self_deactivation`, `staff_last_platform_admin`) necesitan mostrarse
 * en contexto, junto a la persona afectada.
 *
 * La baja es reversible: el backend solo pone `is_active=false` y conserva los
 * grupos, así que la fila sigue en la lista y puede reactivarse.
 */
function ConfirmDeactivateModal({ member, onHide }) {
  const deactivate = useDeactivateStaff();

  useEffect(() => {
    deactivate.reset();
    // Solo al cambiar de miembro: `deactivate` cambia de identidad cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  function handleConfirm() {
    deactivate.mutate(member.id, {
      onSuccess: () => {
        notifySuccess(`${member.email} quedó desactivado.`);
        onHide();
      },
    });
  }

  return (
    <Modal
      show={Boolean(member)}
      onHide={onHide}
      aria-labelledby={HEADING_ID}
      aria-modal="true"
    >
      <Modal.Header closeButton>
        <Modal.Title id={HEADING_ID}>Desactivar miembro</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {deactivate.isError && (
          <Alert variant="danger" className="small">
            {staffApiError(deactivate.error, "No se pudo desactivar la cuenta.")}
          </Alert>
        )}
        <p className="mb-2">
          ¿Desactivar la cuenta de <strong>{member?.name}</strong> (
          {member?.email})?
        </p>
        <p className="text-muted small mb-0">
          Perderá el acceso al panel, pero conserva sus roles: puedes reactivarlo
          más adelante.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={deactivate.isPending}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirm}
          disabled={deactivate.isPending}
          aria-label="Confirmar desactivación del miembro de staff"
        >
          {deactivate.isPending ? <Spinner size="sm" aria-hidden="true" /> : "Desactivar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { ConfirmDeactivateModal };
